import {Box, Typography, Card, CardContent, Alert, AlertTitle} from '@mui/material'
import {styled} from '@mui/material/styles'
import CompareArrowsIcon from '@mui/icons-material/CompareArrows'

import {useMemo} from 'react'
import SelectionAutocomplete from '../../components/SelectionAutocomplete'
import SelectionChips from '../../components/SelectionChips'
import {Metrics, useBenchmarkComparison} from './useBenchmarkComparison'
import MaterialTable from '../../components/MaterialTable'
import {getPerformanceColor} from './PerformanceStyled'

import {metricLookup} from './useBenchmarkComparison'
import ChartGroupToggle from '../../components/ChartGroupToggle'
import CompositChart from '../../components/charts/CompositChart'
import getColor from '../../components/charts/colors'
import {formatNumber} from '../../helpers/UtilityHelper'
import {generateComparisonColumns} from './performanceColumns'
import {serviceTypeLabel} from './serviceTypeLabel'

// ─── Local Styled Components ────────────────────────────────────────────────
const Section = styled(Box)(({theme}) => ({
  padding: theme.spacing(2.5, 3),
  borderBottom: '1px solid #eef2f7',
  backgroundColor: '#ffffff',
}))

const SectionLabel = styled(Typography)({
  fontSize: '0.72rem',
  fontWeight: 600,
  color: '#637381',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  marginBottom: 10,
})

const KpiRow = styled(Box)(({theme}) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
  gap: theme.spacing(1.5),
}))

const KpiCard = styled(Box, {
  shouldForwardProp: (p) => p !== 'accentColor',
})(({accentColor}) => ({
  padding: '14px 12px',
  borderRadius: 8,
  border: '1px solid #e5e7eb',
  borderLeft: `3px solid ${accentColor || '#d1d5db'}`,
  textAlign: 'center',
  backgroundColor: '#fff',
}))

const KpiLabel = styled(Typography)({
  fontSize: '0.6rem',
  fontWeight: 700,
  color: '#9ca3af',
  textTransform: 'uppercase',
  letterSpacing: '0.03em',
  lineHeight: 1.2,
  marginBottom: 6,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
})

const KpiValue = styled(Typography)({
  fontSize: '1.25rem',
  fontWeight: 800,
  lineHeight: 1.2,
})

const KpiCount = styled(Typography)({
  fontSize: '0.62rem',
  color: '#9ca3af',
  fontWeight: 500,
  marginTop: 3,
})

const TableSection = styled(Box)({
  borderBottom: '1px solid #eef2f7',
})

const ChartSection = styled(Box)(({theme}) => ({
  padding: theme.spacing(2.5, 3),
  backgroundColor: '#ffffff',
}))

const ChartHeader = styled(Box)(({theme}) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(2),
}))

const EmptyAlert = styled(Alert)({
  margin: '24px',
  borderRadius: 10,
})

const HeroBanner = styled(Box)(({theme}) => ({
  background: 'linear-gradient(135deg, #1e3a5f 0%, #0f2744 100%)',
  padding: theme.spacing(2.5, 3),
  color: 'white',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  position: 'relative',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: 3,
    background: 'linear-gradient(90deg, #10b981, #f59e0b, #ef4444)',
  },
}))

export function Comparison(props) {
  const {
    data,
    selectedCouriers,
    selectedMetrics,
    selectedTrendAnalysisMetrics,
    setSelectedCouriers,
    setSelectedMetrics,
    setSelectedTrendAnalysisMetrics,
    groupOn,
    setGroupOn,
  } = props

  const {overallSummary, courierSummary, trendAnalysisSummary} = useBenchmarkComparison({
    data,
    selectedCouriers,
    selectedMetrics,
    selectedTrendAnalysisMetrics,
  })

  // selectable units are SERVICE TYPES (the keys of the performance maps), not couriers
  const options = useMemo(() => {
    const keys = new Set()
    ;[data?.deliveryPerformance, data?.tatPerformance, data?.rvpPickupPerformance].forEach((m) => {
      if (m && typeof m.keys === 'function') {
        for (const k of m.keys()) keys.add(k)
      }
    })
    return [...keys].map((serviceType) => ({label: serviceTypeLabel(serviceType), value: serviceType}))
  }, [data])

  const courierSummaryColumns = useMemo(() => {
    return generateComparisonColumns(selectedMetrics)
  }, [selectedMetrics])

  const hasSelections = selectedCouriers.length > 0 && selectedMetrics.length > 0
  const showResults = hasSelections && overallSummary && courierSummary

  const trendAnalysisMetrics = useMemo(
    () => Metrics.filter((metric) => selectedMetrics.includes(metric.value)),
    [selectedMetrics]
  )

  const chartOptions = useMemo(
    () => ({
      layout: 'horizontal',
      xaxis: 'name',
      yaxisLabel: metricLookup.get(selectedTrendAnalysisMetrics?.[0])?.label,
      data: selectedCouriers.map((courier, idx) => ({
        dataKey: courier,
        label: options.find((option) => option.value === courier)?.label,
        chartType: 'line',
        fill: getColor(idx),
      })),
    }),
    [selectedCouriers, options, selectedTrendAnalysisMetrics]
  )

  const sortedCourierSummary = useMemo(() => {
    if (!courierSummary || courierSummary.length === 0 || selectedMetrics.length === 0) {
      return courierSummary
    }
    const firstMetric = selectedMetrics[0]
    return [...courierSummary].sort((a, b) => {
      return (b[firstMetric]?.value || 0) - (a[firstMetric]?.value || 0)
    })
  }, [courierSummary, selectedMetrics])

  return (
    <>
      {/* Hero */}
      <HeroBanner>
        <Box>
          <Typography sx={{fontSize: '0.85rem', fontWeight: 600, mb: 0.5}}>
            Service-Type Performance Comparison
          </Typography>
          <Typography sx={{fontSize: '0.72rem', opacity: 0.7, maxWidth: 500}}>
            Select service types and KPIs to benchmark delivery, TAT, and RVP metrics side-by-side.
          </Typography>
        </Box>
        <CompareArrowsIcon sx={{fontSize: 48, opacity: 0.15}} />
      </HeroBanner>

      {/* Step 1: Courier Selection */}
      <Section>
        <SectionLabel>Step 1 — Select Service Types</SectionLabel>
        <SelectionAutocomplete
          options={options}
          selected={selectedCouriers}
          handleSelectionChange={setSelectedCouriers}
          maxVisibleOptions={4}
        />
      </Section>

      {/* Step 2: Metric Selection */}
      <Section>
        <SectionLabel>Step 2 — Choose Performance Metrics</SectionLabel>
        <SelectionChips
          options={Metrics}
          selected={selectedMetrics}
          handleSelectionChange={setSelectedMetrics}
        />
      </Section>

      {/* Empty state */}
      {!hasSelections && (
        <EmptyAlert severity='info'>
          <AlertTitle sx={{fontWeight: 700, fontSize: '0.85rem'}}>Get Started</AlertTitle>
          <Typography variant='body2'>
            Select at least one service type and one metric above to see comparison results.
          </Typography>
        </EmptyAlert>
      )}

      {/* Results */}
      {showResults && overallSummary && overallSummary.length > 0 && (
        <>
          {/* KPI Summary */}
          <Section>
            <SectionLabel>Overall Averages</SectionLabel>
            <KpiRow>
              {overallSummary.map((row, idx) => {
                const v = typeof row.value === 'number' ? row.value : 0
                const color = getPerformanceColor(v)
                return (
                  <KpiCard key={idx} accentColor={color}>
                    <KpiLabel title={row.label}>{row.label}</KpiLabel>
                    <KpiValue sx={{color}}>{typeof row.value === 'number' ? `${row.value.toFixed(1)}%` : row.value}</KpiValue>
                    <KpiCount>{formatNumber(row.count)} orders</KpiCount>
                  </KpiCard>
                )
              })}
            </KpiRow>
          </Section>

          {/* Courier Comparison Table */}
          <Section sx={{p: 0}}>
            <Box sx={{px: 3, pt: 2.5, pb: 1}}>
              <SectionLabel>Service-type Comparison</SectionLabel>
            </Box>
            <TableSection>
              <MaterialTable
                rows={sortedCourierSummary}
                columns={[
                  {
                    value: (row) => (
                      <Typography variant='body2' fontWeight={600} noWrap sx={{minWidth: 160}}>
                        {row.label}
                      </Typography>
                    ),
                    label: 'Service Type',
                    align: 'left',
                  },
                  ...courierSummaryColumns,
                ]}
                size='medium'
                itemsPerPage={20}
                frozenColumnsCount={0}
                noDataMessage='No comparison data available'
              />
            </TableSection>
          </Section>

          {/* Trend Analysis */}
          <ChartSection>
            <ChartHeader>
              <Box>
                <SectionLabel sx={{mb: 1}}>Trend Analysis</SectionLabel>
                <SelectionChips
                  exclusive
                  options={trendAnalysisMetrics}
                  selected={selectedTrendAnalysisMetrics}
                  handleSelectionChange={setSelectedTrendAnalysisMetrics}
                />
              </Box>
              <ChartGroupToggle setGroupOn={setGroupOn} groupOn={groupOn} />
            </ChartHeader>
            <Box sx={{height: 350}}>
              <CompositChart data={trendAnalysisSummary} chartOptions={chartOptions} height={350} />
            </Box>
          </ChartSection>
        </>
      )}
    </>
  )
}
